import {
  useContext,
  createContext,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { SubmenuItem } from '@lglp/prismic-client';
import { disableGlobalScroll } from '@lglp/utils';
import { useLocation } from 'react-router';

export enum MobileMenuPart {
  left = 'left',
  right = 'right',
}

export const MobileContext = createContext<MobileContextProps | undefined>(
  undefined
);

export const useMobileContext = (): MobileContextProps => {
  const context = useContext(MobileContext);
  if (!context) {
    throw new Error('useDesktopContext must be inside a MobileLayout');
  }
  return context;
};

type MobileContextProps = {
  opened: boolean;
  displayedMenuPart: string;
  selectedSubmenu: SubmenuItem;

  toggleMenu: (value: boolean) => void;
  switchToMenuPart: (value: MobileMenuPart) => void;
  selectSubmenuItem: (value: SubmenuItem) => void;
};

export const useInitializeContext = (): MobileContextProps => {
  const [opened, setOpened] = useState<boolean>(false);
  const [displayedMenuPart, setDisplayedMenuPart] = useState<MobileMenuPart>(
    MobileMenuPart.left
  );
  const [selectedSubmenu, setSelectedSubmenu] = useState<SubmenuItem>();

  const location = useLocation();

  // Disable global scroll when the menu is opened
  useEffect(() => {
    disableGlobalScroll(!!opened);

    if (opened) {
      window.scrollTo(0, 0);
    }

    return () => {
      disableGlobalScroll(false);
    };
  }, [opened]);

  // Close the menu when location has changed
  useEffect(() => {
    setOpened(false);
    setDisplayedMenuPart(MobileMenuPart.left);
    setSelectedSubmenu(undefined);
  }, [location]);

  const toggleMenu = useCallback((value) => {
    setOpened(value);
    setDisplayedMenuPart(MobileMenuPart.left);
    setSelectedSubmenu(undefined);
  }, []);

  const switchToMenuPart = useCallback((value) => {
    setDisplayedMenuPart(value);
  }, []);

  const selectSubmenuItem = useCallback((value) => {
    setSelectedSubmenu(value);
  }, []);

  return {
    opened,
    displayedMenuPart,
    selectedSubmenu,
    toggleMenu,
    switchToMenuPart,
    selectSubmenuItem,
  };
};
